import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Bot,
  Check,
  CheckCircle2,
  CreditCard,
  DollarSign,
  Layers3,
  Layout,
  Loader2,
  MessageSquareText,
  MousePointerClick,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Workflow,
  Zap,
} from "lucide-react";
import { API_BASE } from "@/lib/api";
import { Link, useLocation, useParams } from "@/lib/router";
import { trackCtaClick, trackLandingView } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  genericLandingProofs,
  landingKey,
  productLandingCopy,
  productLandingProofs,
  productLandingVariants,
  type LandingCopy,
  type LandingProof,
  type LandingVariant,
} from "./productLandingCopy";

type ProductKind = "web" | "messaging";

type ProductUnit = {
  id: string;
  name: string;
  description: string;
  risk: "low" | "medium" | "high";
  disclaimer?: string;
  serviceIds?: string[];
  requiresConsent?: boolean;
  requiresHumanApproval?: boolean;
  requiresHumanReview?: boolean;
  requiresPayment?: boolean;
};

type Product = {
  id: string;
  name: string;
  priority: "P0" | "P1" | "P2" | "P3" | string;
  segment: string;
  positioning: string;
  category?: string;
  serviceIds?: string[];
  modules?: ProductUnit[];
  tools?: ProductUnit[];
  workflow?: string[];
  useCases?: string[];
  guardrails: string[];
  disclaimers?: string[];
  pricing: Array<{ name: string; priceUsd: string; limits: Record<string, number | string> }>;
};

export function AiProductLanding() {
  const { productKind = "web", productId = "" } = useParams<{ productKind: ProductKind; productId: string }>();
  const kind: ProductKind = productKind === "messaging" ? "messaging" : "web";
  const prefix = kind === "web" ? "web-products" : "products";
  const key = landingKey(kind, productId);
  const location = useLocation();
  const variant = useLandingVariant(key);

  const { data: product, isLoading, isError, error, refetch } = useQuery<Product>({
    queryKey: ["ai-product-landing", kind, productId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${prefix}/products/${productId}`);
      if (!res.ok) throw new Error("Product not found");
      const body = await res.json();
      return body.product;
    },
  });

  const copy = useMemo(() => (product ? applyVariant(getCopy(kind, product), variant) : undefined), [kind, product, variant]);
  const proofCards = useMemo(() => productLandingProofs[key] || genericLandingProofs, [key]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [kind, productId]);

  useEffect(() => {
    if (!product) return;
    trackLandingView({ productId: product.id, productKind: kind, variant: variant.id, priority: product.priority });
  }, [kind, product, variant.id]);

  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-cyan-200" />
      </div>
    );
  }

  if (isError || !product || !copy) {
    return (
      <div className="mx-auto max-w-3xl rounded-[2rem] border border-rose-200/25 bg-rose-500/10 p-8 text-rose-50">
        <h1 className="text-2xl font-black">No se pudo cargar la landing</h1>
        <p className="mt-3 text-sm leading-6 text-rose-100/85">
          {error instanceof Error ? error.message : "La API no respondió correctamente."}
        </p>
        <Button className="mt-6 rounded-full bg-white px-6 text-slate-950 hover:bg-rose-100" onClick={() => refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  const units = kind === "web" ? product.modules || [] : product.tools || [];
  const paidTier = product.pricing.find((tier) => tier.priceUsd !== "0") || product.pricing[0];
  const priceLabel = paidTier ? formatPrice(paidTier.priceUsd) : "Precio por definir";
  const useProductPath = kind === "messaging" && product.id === "tiendita-bot-os"
    ? "/tienditabot"
    : `/ai-suite/products/${kind}/${product.id}`;
  const productLabel = kind === "web" ? "Producto Web" : "WhatsApp / Telegram";
  const workflow = product.workflow?.length ? product.workflow : defaultWorkflow(kind);
  const disclaimers = Array.from(new Set([...(product.disclaimers || []), ...units.map((unit) => unit.disclaimer).filter(Boolean) as string[]]));
  const trackLandingCta = (cta: string, destination: string, locationName: string) => {
    trackCtaClick({
      productId: product.id,
      productKind: kind,
      variant: variant.id,
      cta,
      destination,
      location: locationName,
    });
  };

  if (kind === "messaging" && product.id === "tiendita-bot-os") {
    return (
      <TienditaBotLanding
        product={product}
        useProductPath={useProductPath}
        variantId={variant.id}
        trackLandingCta={trackLandingCta}
      />
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-10 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" asChild className="rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
          <Link to="/ai-suite" onClick={() => trackLandingCta("back_to_suite", "/ai-suite", "top_nav")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a la suite
          </Link>
        </Button>
        <Button asChild variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
          <Link to={useProductPath} onClick={() => trackLandingCta("open_product", useProductPath, "top_nav")}>
            Abrir producto operativo
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>

      <section className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.055] p-6 shadow-2xl sm:p-8 lg:p-10">
        <div className="absolute -right-20 -top-24 h-80 w-80 rounded-full bg-cyan-300/15 blur-3xl" />
        <div className="absolute -bottom-28 left-16 h-72 w-72 rounded-full bg-amber-300/10 blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge tone="cyan">{copy.eyebrow}</Badge>
              <Badge>{productLabel}</Badge>
              <Badge>{product.priority}</Badge>
              <Badge>{`${units.length} herramientas`}</Badge>
              <Badge tone="gold">{`A/B ${variant.id}`}</Badge>
            </div>
            <div className="space-y-4">
              <h1 className="max-w-5xl text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
                {copy.headline}
              </h1>
              <p className="max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">
                {copy.subheadline}
              </p>
            </div>
            <div className="rounded-3xl border border-cyan-200/20 bg-cyan-300/10 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-cyan-100/70">Promesa de venta</p>
              <p className="mt-2 text-lg font-black leading-7 text-cyan-50">{copy.promise}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="h-12 rounded-full bg-cyan-200 px-6 font-black text-slate-950 hover:bg-white">
                <Link to={useProductPath} onClick={() => trackLandingCta(copy.ctaLabel, useProductPath, "hero_primary")}>
                  {copy.ctaLabel}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <a href="#tools">
                <Button
                  variant="outline"
                  className="h-12 rounded-full border-white/15 bg-white/5 px-6 text-white hover:bg-white/10"
                  onClick={() => trackLandingCta(copy.secondaryCtaLabel, `${location.pathname}#tools`, "hero_secondary")}
                >
                  {copy.secondaryCtaLabel}
                </Button>
              </a>
            </div>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-black/25 p-5 backdrop-blur">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-200 text-slate-950">
                {kind === "web" ? <Layers3 className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
              </div>
              <div>
                <p className="text-sm font-black text-white">Landing lista para pauta</p>
                <p className="text-sm text-slate-400">Ruta de adquisición separada del workspace operativo.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3">
              <Metric icon={Target} label="Audiencia" value={copy.audience} />
              <Metric icon={CreditCard} label="Precio de entrada" value={priceLabel} />
              <Metric icon={MousePointerClick} label="Ángulo de pauta" value={copy.trafficAngle} />
              <Metric icon={Sparkles} label="Variante activa" value={`${variant.name}: ${variant.rationale}`} />
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-2">
        <Card className="rounded-[2rem] border-rose-200/15 bg-rose-500/[0.055]">
          <CardContent className="p-6">
            <SectionKicker icon={AlertTriangle} label="Problema que compra" tone="rose" />
            <h2 className="mt-4 text-3xl font-black text-white">Por qué alguien haría click y pagaría.</h2>
            <div className="mt-5 grid gap-3">
              {copy.painPoints.map((item) => (
                <Bullet key={item} tone="rose">{item}</Bullet>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[2rem] border-emerald-200/15 bg-emerald-500/[0.055]">
          <CardContent className="p-6">
            <SectionKicker icon={CheckCircle2} label="Resultado después del pago" tone="emerald" />
            <h2 className="mt-4 text-3xl font-black text-white">Qué recibe el cliente realmente.</h2>
            <div className="mt-5 grid gap-3">
              {copy.outcomes.map((item) => (
                <Bullet key={item} tone="emerald">{item}</Bullet>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-white/[0.045] p-6 shadow-xl">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionKicker icon={Zap} label="Demo mental para tráfico frío" tone="cyan" />
            <h2 className="mt-4 text-3xl font-black text-white">Un usuario debe entender el valor sin llamada de ventas.</h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">{copy.demoScenario}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {copy.proofPoints.map((item) => <Badge key={item} tone="gold">{item}</Badge>)}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["Pago protegido", "Checkout, runToken, suscripción o workspace antes de ejecutar lo pagado."],
              ["Anti-fraude operativo", "Idempotency-key, input hash, rate limits y auditoría por run."],
              ["Backend reutilizable", "Workers existentes, artifacts, scheduler, logs y stores persistentes."],
              ["Control humano", "Aprobación para legal, salud, broadcasts, cobros o acciones sensibles."],
            ].map(([title, body]) => (
              <div key={title} className="rounded-3xl border border-white/10 bg-black/20 p-5">
                <p className="font-black text-white">{title}</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="space-y-5 rounded-[2rem] border border-amber-200/15 bg-amber-300/[0.045] p-6 shadow-xl">
        <div className="grid gap-4 lg:grid-cols-[0.75fr_1.25fr] lg:items-end">
          <div>
            <SectionKicker icon={BarChart3} label="Prueba visual para anuncios" tone="gold" />
            <h2 className="mt-4 text-3xl font-black text-white">Demo screenshots sin inventar clientes.</h2>
            <p className="mt-3 text-sm leading-7 text-amber-50/85">
              Hasta tener casos reales, la pauta debe mostrar capturas demo honestas: input, output esperado y métrica operacional clara.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-black/25 p-4 text-sm leading-6 text-slate-300">
            Tracking activo si configuras <code className="text-cyan-100">VITE_GA_MEASUREMENT_ID</code> o <code className="text-cyan-100">VITE_META_PIXEL_ID</code>.
            Cada CTA envía <code className="text-cyan-100">cta_click</code> con producto, tipo, variante y ubicación.
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {proofCards.map((proof) => (
            <ProofCard key={proof.title} proof={proof} />
          ))}
        </div>
      </section>

      <section id="tools" className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <SectionKicker icon={Workflow} label="Herramientas incluidas" tone="cyan" />
            <h2 className="mt-3 text-3xl font-black text-white">{product.name} no es una tool suelta.</h2>
          </div>
          <Button asChild variant="outline" className="h-11 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
            <Link to={useProductPath} onClick={() => trackLandingCta("use_now_tools", useProductPath, "tools_header")}>Usar ahora</Link>
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {units.map((unit) => (
            <div key={unit.id} className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-black text-white">{unit.name}</h3>
                <RiskBadge risk={unit.risk} />
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-300">{unit.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {unit.requiresPayment !== false && kind === "web" ? <MiniPill>pago</MiniPill> : null}
                {unit.requiresHumanApproval || unit.requiresHumanReview ? <MiniPill>aprobación humana</MiniPill> : null}
                {unit.requiresConsent ? <MiniPill>consentimiento</MiniPill> : null}
                {(unit.serviceIds || []).slice(0, 3).map((serviceId) => <MiniPill key={serviceId}>{serviceId}</MiniPill>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-[1fr_0.85fr]">
        <div className="rounded-[2rem] border border-white/10 bg-white/[0.045] p-6">
          <SectionKicker icon={Workflow} label="Flujo de conversión" tone="emerald" />
          <div className="mt-5 space-y-4">
            {workflow.map((step, index) => (
              <div key={step} className="grid grid-cols-[2.5rem_1fr] gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-200 font-black text-slate-950">{index + 1}</div>
                <p className="pt-1.5 text-sm leading-6 text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] border border-cyan-200/15 bg-cyan-300/[0.045] p-6">
          <SectionKicker icon={CreditCard} label="Precios" tone="cyan" />
          <div className="mt-5 grid gap-3">
            {product.pricing.map((tier) => (
              <div key={tier.name} className="rounded-3xl border border-white/10 bg-black/25 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-black text-white">{tier.name}</p>
                  <p className="text-lg font-black text-cyan-100">{formatPrice(tier.priceUsd)}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(tier.limits).slice(0, 4).map(([key, value]) => <MiniPill key={key}>{`${key}: ${String(value)}`}</MiniPill>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-[2rem] border border-white/10 bg-white/[0.045] p-6">
          <SectionKicker icon={MessageSquareText} label="FAQ para pauta" tone="gold" />
          <div className="mt-5 space-y-3">
            {copy.faqs.map((item) => (
              <div key={item.question} className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <p className="font-black text-white">{item.question}</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] border border-amber-200/15 bg-amber-300/[0.045] p-6">
          <SectionKicker icon={ShieldCheck} label="Confianza y límites" tone="gold" />
          <p className="mt-4 text-sm leading-7 text-amber-50/85">
            Esta página vende el resultado, pero no elimina controles. Los módulos sensibles siguen teniendo disclaimers, aprobación humana, auditoría y límites de uso.
          </p>
          <div className="mt-5 space-y-3">
            {(disclaimers.length ? disclaimers : product.guardrails).slice(0, 5).map((item) => (
              <div key={item} className="rounded-2xl border border-amber-200/15 bg-black/20 p-3 text-sm leading-6 text-amber-50/90">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-cyan-300/15 via-white/[0.055] to-amber-300/10 p-7 text-center shadow-2xl">
        <p className="text-xs uppercase tracking-[0.24em] text-cyan-100/70">Siguiente paso</p>
        <h2 className="mx-auto mt-3 max-w-3xl text-3xl font-black text-white sm:text-4xl">
          Si el anuncio trae el dolor correcto, esta landing debe llevar al usuario directo al producto.
        </h2>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild className="h-12 rounded-full bg-cyan-200 px-6 font-black text-slate-950 hover:bg-white">
            <Link to={useProductPath} onClick={() => trackLandingCta(copy.ctaLabel, useProductPath, "final_primary")}>
              {copy.ctaLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-12 rounded-full border-white/15 bg-white/5 px-6 text-white hover:bg-white/10">
            <Link to="/ai-suite" onClick={() => trackLandingCta("compare_products", "/ai-suite", "final_secondary")}>Comparar productos</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function TienditaBotLanding({
  product,
  useProductPath,
  variantId,
  trackLandingCta,
}: {
  product: Product;
  useProductPath: string;
  variantId: string;
  trackLandingCta: (cta: string, destination: string, locationName: string) => void;
}) {
  const ctaLabel = "Crear mi tienda por WhatsApp";
  const tiers = normalizeTienditaTiers(product.pricing);
  const popularTools = normalizeTienditaTools(product.tools || []);

  return (
    <div className="-mx-4 -my-6 min-h-screen overflow-x-hidden bg-[#0a0c10] font-sans text-gray-100 selection:bg-emerald-500/30 sm:-mx-6 lg:-mx-8">
      <div className="pointer-events-none fixed left-1/2 top-0 -z-10 h-screen w-full -translate-x-1/2 bg-emerald-500/5 blur-[120px]" />

      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0c10]/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <Link
            to="/ai-suite"
            className="group flex items-center gap-2 text-sm font-medium text-gray-400 transition-colors hover:text-white"
            onClick={() => trackLandingCta("back_to_suite", "/ai-suite", "code_back")}
          >
            <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
            <span>Regresar</span>
          </Link>
          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2 text-lg font-bold tracking-tight">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-500 text-[10px] text-black">TB</span>
            TienditaBot <span className="font-black text-emerald-500">OS</span>
          </div>
          <div className="w-10" />
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-32 px-6 py-12">
        <section className="space-y-12 text-center">
          <div className="relative animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="group relative mx-auto aspect-[16/10] w-full max-w-2xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-gray-800 to-gray-900 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(16,185,129,0.28),transparent_36%),linear-gradient(to_top,#0a0c10,transparent_56%)] transition-opacity duration-700 group-hover:opacity-95" />
              <div className="absolute inset-0 flex items-center justify-center gap-6 p-8">
                <div className="relative flex h-[80%] w-1/4 flex-col overflow-hidden rounded-[2.5rem] border-[6px] border-[#1a1d23] bg-[#0f1115] shadow-2xl">
                  <div className="mx-auto h-6 w-1/2 shrink-0 rounded-b-xl bg-[#1a1d23]" />
                  <div className="flex-1 space-y-3 overflow-hidden p-3">
                    <div className="flex h-8 w-full items-center rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2">
                      <div className="h-2 w-1/2 rounded bg-emerald-500/30" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-20 w-3/4 rounded-xl bg-gray-800/40" />
                      <div className="ml-auto h-20 w-2/3 rounded-xl bg-emerald-900/20" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="h-12 rounded-lg bg-white/10" />
                      <div className="h-12 rounded-lg bg-white/10" />
                      <div className="h-12 rounded-lg bg-white/10" />
                    </div>
                  </div>
                </div>

                <div className="relative flex h-[90%] w-3/5 flex-col overflow-hidden rounded-[2rem] border-[8px] border-[#1a1d23] bg-[#0f1115] shadow-2xl">
                  <div className="flex shrink-0 items-center justify-between border-b border-white/5 p-4">
                    <div className="h-3 w-24 rounded bg-white/10" />
                    <div className="flex gap-1">
                      <div className="h-2 w-2 rounded-full bg-gray-700" />
                      <div className="h-2 w-2 rounded-full bg-gray-700" />
                    </div>
                  </div>
                  <div className="grid flex-1 grid-cols-3 gap-3 overflow-hidden p-4">
                    <div className="space-y-2">
                      <div className="h-20 rounded-xl border border-emerald-500/20 bg-emerald-500/10" />
                      <div className="h-10 rounded-lg bg-white/5" />
                      <div className="h-10 rounded-lg bg-white/5" />
                    </div>
                    <div className="col-span-2 space-y-3 rounded-xl border border-white/5 bg-white/5 p-3">
                      <div className="h-3 w-1/3 rounded bg-white/20" />
                      <div className="space-y-2">
                        {[1, 2, 3, 4].map((item) => (
                          <div key={item} className="flex justify-between">
                            <div className="h-2 w-2/3 rounded bg-white/10" />
                            <div className="h-2 w-8 rounded bg-emerald-500/30" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-500/20 blur-[120px] mix-blend-screen" />
            </div>
          </div>

          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="bg-gradient-to-b from-white to-gray-500 bg-clip-text text-5xl font-black leading-[1.1] tracking-tight text-transparent md:text-6xl">
              Vende por WhatsApp <br className="hidden md:block" /> como un Profesional
            </h1>
            <p className="mx-auto max-w-xl text-xl font-medium leading-relaxed text-gray-400">
              Transforma tus chats en ventas automatizadas sin esfuerzo.
            </p>
          </div>

          <Link
            to={useProductPath}
            className="inline-flex transform rounded-2xl bg-emerald-500 px-10 py-5 text-lg font-black uppercase tracking-tight text-black shadow-[0_0_50px_-5px_rgba(16,185,129,0.6)] transition-all hover:scale-105 hover:bg-emerald-400 active:scale-95"
            onClick={() => trackLandingCta(ctaLabel, useProductPath, `code_hero_${variantId}`)}
          >
            {ctaLabel}
          </Link>
        </section>

        <section className="space-y-16 py-12">
          <div className="space-y-2 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white">¿Cómo funciona?</h2>
            <div className="mx-auto h-1 w-20 rounded-full bg-emerald-500" />
          </div>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            <TienditaStepCard icon={<Users className="text-emerald-500" size={32} />} step="1" title="Regístrate en TienditaBot" />
            <TienditaStepCard icon={<Layout className="text-emerald-500" size={32} />} step="2" title="Sube tu catálogo" />
            <TienditaStepCard icon={<DollarSign className="text-emerald-500" size={32} />} step="3" title="Comienza a vender en minutos" />
          </div>
        </section>

        <section className="space-y-12">
          <h2 className="text-center text-3xl font-bold tracking-tight text-white">Herramientas Populares</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {popularTools.map((tool, index) => (
              <TienditaToolCard
                key={tool.id}
                title={tool.name}
                description={tool.description}
                badge={index === 0 ? "Nuevo" : tool.risk}
              />
            ))}
          </div>
        </section>

        <section className="space-y-16 py-12">
          <div className="space-y-4 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white">Comparar Planes</h2>
            <p className="text-gray-500">Escoge el plan perfecto para el tamaño de tu negocio.</p>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[680px] overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.02] shadow-2xl backdrop-blur-2xl sm:min-w-[800px]">
              <div className="grid grid-cols-4 border-b border-white/5 bg-white/[0.02]">
                <div className="p-8" />
                {tiers.map((tier) => (
                  <TienditaPlanHeader key={tier.name} name={displayTienditaTierName(tier.name)} isPopular={tier.name.toLowerCase() === "starter"} />
                ))}
              </div>

              <div className="divide-y divide-white/5">
                <TienditaPriceRow label="Funciones Básicas" values={["Incluido", "Premium", "Full Access"]} />
                <TienditaPriceRow label="Catálogo Digital" values={[true, true, true]} />
                <TienditaPriceRow label="Gestión de Clientes" values={[false, true, true]} />
                <TienditaPriceRow label="Automatización de Ventas" values={[false, true, true]} />
                <TienditaPriceRow label="Soporte Prioritario" values={[false, false, true]} />

                <div className="grid grid-cols-4 items-center bg-white/[0.03]">
                  <div className="p-8 text-sm font-bold uppercase tracking-widest text-gray-500">Precio Final</div>
                  {tiers.map((tier) => (
                    <div
                      key={`${tier.name}-price`}
                      className={`p-8 text-center text-xl font-black ${tier.name.toLowerCase() === "starter" ? "bg-emerald-500/5 text-emerald-500" : ""}`}
                    >
                      {formatPrice(tier.priceUsd)}
                      {tier.priceUsd !== "0" ? (
                        <span className={`text-xs font-medium uppercase ${tier.name.toLowerCase() === "starter" ? "text-emerald-500/50" : "text-gray-600"}`}>/mes</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-32 pt-12">
          <div className="relative space-y-8 overflow-hidden rounded-[3rem] border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-transparent p-12 text-center">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.1)_0%,transparent_70%)]" />
            <h2 className="relative text-4xl font-bold tracking-tight text-white">¿Listo para escalar tu negocio?</h2>
            <Link
              to={useProductPath}
              className="relative inline-flex transform rounded-2xl bg-emerald-500 px-12 py-6 text-xl font-black text-black shadow-[0_20px_50px_-10px_rgba(16,185,129,0.4)] transition-all hover:scale-[1.02] hover:bg-emerald-400 active:scale-95"
              onClick={() => trackLandingCta("Crear mi tienda por WhatsApp ahora", useProductPath, `code_final_${variantId}`)}
            >
              Crear mi tienda por WhatsApp ahora
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 px-6 py-12 text-center text-sm text-gray-600">
        <p>© 2026 {product.name}. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}

function normalizeTienditaTiers(pricing: Product["pricing"]) {
  const byName = new Map(pricing.map((tier) => [tier.name.toLowerCase(), tier]));
  const fallback = [
    { name: "Free", priceUsd: "0", limits: { products: 15, ordersPerMonth: 30 } },
    { name: "Starter", priceUsd: "9-19", limits: { products: 100, broadcastsPerMonth: 4 } },
    { name: "Pro", priceUsd: "29-59", limits: { products: "unlimited", automation: "enabled" } },
  ];
  return ["free", "starter", "pro"].map((name, index) => byName.get(name) || fallback[index]);
}

function normalizeTienditaTools(tools: ProductUnit[]) {
  if (!tools.length) {
    return [
      { id: "catalog_builder", name: "WhatsApp Store Builder", description: "Crea tu catálogo, productos y tienda personalizada en segundos.", risk: "low" as const },
      { id: "inventory_manager", name: "Inventory Manager", description: "Sincronización en tiempo real para stock, ventas y notificaciones.", risk: "low" as const },
    ];
  }
  const preferred = ["catalog_builder", "inventory_manager"];
  const ordered = [
    ...preferred.map((id) => tools.find((tool) => tool.id === id)).filter(Boolean),
    ...tools.filter((tool) => !preferred.includes(tool.id)),
  ] as ProductUnit[];
  return ordered.slice(0, 2);
}

function displayTienditaTierName(name: string) {
  return name.toLowerCase() === "free" ? "Gratis" : name;
}

function TienditaStepCard({ icon, step, title }: { icon: ReactNode; step: string; title: string }) {
  return (
    <div className="group flex flex-col items-center space-y-6 text-center transition-transform hover:-translate-y-1">
      <div className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-gray-900 shadow-2xl transition-all group-hover:border-emerald-500/50 group-hover:bg-[#15181e]">
        <div className="absolute inset-0 bg-emerald-500/5 opacity-0 transition-opacity group-hover:opacity-100" />
        {icon}
      </div>
      <div className="space-y-2">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-500/60">{step}. Paso</p>
        <p className="px-4 text-lg font-bold leading-snug text-gray-100">{title}</p>
      </div>
    </div>
  );
}

function TienditaToolCard({ title, description, badge }: { title: string; description: string; badge?: string }) {
  return (
    <div className="group flex cursor-pointer items-center justify-between rounded-3xl border border-white/5 bg-white/[0.02] p-8 transition-all hover:border-emerald-500/30 hover:bg-white/[0.05]">
      <div className="space-y-2 text-left">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-white transition-colors group-hover:text-emerald-400">{title}</h3>
          {badge ? (
            <span className="rounded-md bg-emerald-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-black">
              {badge}
            </span>
          ) : null}
        </div>
        <p className="max-w-sm leading-relaxed text-gray-500">{description}</p>
      </div>
      <div className="ml-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/5 text-gray-600 transition-all group-hover:bg-emerald-500/10 group-hover:text-emerald-500">
        <Zap size={24} />
      </div>
    </div>
  );
}

function TienditaPlanHeader({ name, isPopular }: { name: string; isPopular?: boolean }) {
  return (
    <div className={`space-y-2 p-8 text-center ${isPopular ? "bg-white/5" : ""}`}>
      <p className="text-xl font-bold text-white">{name}</p>
      {isPopular ? (
        <span className="mx-auto block w-fit rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-emerald-500">
          Recomendado
        </span>
      ) : null}
    </div>
  );
}

function TienditaPriceRow({ label, values }: { label: string; values: Array<string | boolean> }) {
  return (
    <div className="grid grid-cols-4 items-center transition-colors">
      <div className="p-6 text-sm font-bold uppercase tracking-widest text-gray-500">{label}</div>
      {values.map((value, index) => (
        <div key={`${label}-${index}`} className={`p-6 text-center ${index === 1 ? "bg-white/5" : ""}`}>
          {typeof value === "boolean" ? (
            value ? (
              <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/10">
                <Check className="text-emerald-500" size={14} strokeWidth={4} />
              </div>
            ) : (
              <span className="text-xl text-gray-800">-</span>
            )
          ) : (
            <span className="text-sm font-bold text-gray-300">{value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function getCopy(kind: ProductKind, product: Product): LandingCopy {
  const direct = productLandingCopy[landingKey(kind, product.id)];
  if (direct) return direct;
  const units = kind === "web" ? product.modules || [] : product.tools || [];
  return {
    eyebrow: product.priority === "P0" || product.priority === "P1" ? "Producto cerca de venta" : "Producto experimental",
    headline: `${product.name} convierte ${product.segment.toLowerCase()} en un flujo operativo pagable.`,
    subheadline: product.positioning,
    promise: "Un solo producto con workspace, herramientas integradas, control de pagos y auditoría.",
    audience: product.segment,
    ctaLabel: "Probar producto",
    secondaryCtaLabel: "Ver herramientas",
    painPoints: [
      "El usuario no quiere entender una lista de microtools.",
      "El flujo de compra necesita explicar resultado, precio y próximos pasos.",
      "Las operaciones sensibles requieren control humano y trazabilidad.",
      "Sin landing, la pauta fría llega a un catálogo demasiado técnico.",
    ],
    outcomes: units.slice(0, 4).map((unit) => unit.description),
    proofPoints: ["Workspace", "Checkout", "Auditoría", "Workers reales"],
    demoScenario: `Selecciona una herramienta de ${product.name}, agrega input y ejecuta el flujo operativo.`,
    trafficAngle: "Pauta a un dolor concreto con CTA directo al producto operativo.",
    faqs: [
      { question: "Esto ya funciona?", answer: "El producto tiene backend y ruta operativa. Algunas capacidades dependen de claves/provider/canal según el módulo." },
      { question: "Puedo pagar?", answer: "Sí, el backend soporta checkout, suscripción/workspace y control de uso." },
      { question: "Qué pasa con casos sensibles?", answer: "Se aplican disclaimers, revisión humana y guardrails por producto." },
    ],
  };
}

function useLandingVariant(key: string): LandingVariant {
  const location = useLocation();
  const [variant, setVariant] = useState<LandingVariant>(() => selectLandingVariant(key, location.search));

  useEffect(() => {
    setVariant(selectLandingVariant(key, location.search));
  }, [key, location.search]);

  return variant;
}

function selectLandingVariant(key: string, search: string): LandingVariant {
  const variants = productLandingVariants[key] || [controlVariant()];
  const queryVariant = new URLSearchParams(search).get("variant")?.toLowerCase();
  const forced = variants.find((variant) => variant.id === queryVariant);
  if (forced) {
    persistVariant(key, forced.id);
    return forced;
  }

  const stored = readStoredVariant(key);
  const storedMatch = variants.find((variant) => variant.id === stored);
  if (storedMatch) return storedMatch;

  const visitorId = getVisitorId();
  const selected = variants[hashToIndex(`${key}:${visitorId}`, variants.length)] || variants[0] || controlVariant();
  persistVariant(key, selected.id);
  return selected;
}

function applyVariant(copy: LandingCopy, variant: LandingVariant): LandingCopy {
  const { id: _id, name: _name, rationale: _rationale, ...overrides } = variant;
  void _id;
  void _name;
  void _rationale;
  return {
    ...copy,
    ...overrides,
    painPoints: variant.painPoints || copy.painPoints,
    outcomes: variant.outcomes || copy.outcomes,
    proofPoints: variant.proofPoints || copy.proofPoints,
    faqs: variant.faqs || copy.faqs,
  };
}

function controlVariant(): LandingVariant {
  return { id: "control", name: "Control", rationale: "Copy base del producto." };
}

function readStoredVariant(key: string) {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(`ai-suite-ab-${key}`) || "";
}

function persistVariant(key: string, variantId: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(`ai-suite-ab-${key}`, variantId);
}

function getVisitorId() {
  if (typeof window === "undefined") return "server";
  const existing = window.localStorage.getItem("ai-suite-visitor-id");
  if (existing) return existing;
  const generated = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem("ai-suite-visitor-id", generated);
  return generated;
}

function hashToIndex(value: string, modulo: number) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return modulo > 0 ? hash % modulo : 0;
}

function ProofCard({ proof }: { proof: LandingProof }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/25 shadow-xl">
      <div className="border-b border-white/10 bg-white/[0.045] p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-black text-white">{proof.title}</p>
            <p className="mt-1 text-xs leading-5 text-slate-400">{proof.caption}</p>
          </div>
          <span className="rounded-full border border-amber-200/25 bg-amber-300/10 px-2.5 py-1 text-xs font-black text-amber-100">{proof.metric}</span>
        </div>
      </div>
      <div className="space-y-3 p-4">
        <div className="rounded-2xl border border-cyan-200/15 bg-cyan-300/10 p-3">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.2em] text-cyan-100/70">Input demo</p>
          <p className="mt-2 text-sm leading-6 text-cyan-50">{proof.sampleInput}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200/15 bg-emerald-300/10 p-3">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.2em] text-emerald-100/70">Output esperado</p>
          <p className="mt-2 text-sm leading-6 text-emerald-50">{proof.sampleOutput}</p>
        </div>
      </div>
    </div>
  );
}

function SectionKicker({ icon: Icon, label, tone }: { icon: typeof Sparkles; label: string; tone: "cyan" | "emerald" | "rose" | "gold" }) {
  const toneClass = {
    cyan: "border-cyan-200/20 bg-cyan-300/10 text-cyan-100",
    emerald: "border-emerald-200/20 bg-emerald-300/10 text-emerald-100",
    rose: "border-rose-200/20 bg-rose-300/10 text-rose-100",
    gold: "border-amber-200/20 bg-amber-300/10 text-amber-100",
  }[tone];
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black ${toneClass}`}>
      <Icon className="h-4 w-4" />
      {label}
    </div>
  );
}

function Metric({ icon: Icon, label, value }: { icon: typeof Sparkles; label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
        <Icon className="h-4 w-4 text-cyan-200" />
        {label}
      </div>
      <p className="mt-2 text-sm font-bold leading-6 text-white">{value}</p>
    </div>
  );
}

function Bullet({ children, tone }: { children: string; tone: "rose" | "emerald" }) {
  const iconClass = tone === "rose" ? "text-rose-200" : "text-emerald-200";
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm leading-6 text-slate-200">
      <CheckCircle2 className={`mt-1 h-4 w-4 shrink-0 ${iconClass}`} />
      <span>{children}</span>
    </div>
  );
}

function Badge({ children, tone = "slate" }: { children: string | number; tone?: "slate" | "cyan" | "gold" }) {
  const toneClass = {
    slate: "border-white/10 bg-white/5 text-slate-300",
    cyan: "border-cyan-200/25 bg-cyan-300/10 text-cyan-100",
    gold: "border-amber-200/25 bg-amber-300/10 text-amber-100",
  }[tone];
  return <span className={`rounded-full border px-3 py-1 text-xs font-black ${toneClass}`}>{children}</span>;
}

function MiniPill({ children }: { children: string }) {
  return <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-300">{children}</span>;
}

function RiskBadge({ risk }: { risk: ProductUnit["risk"] }) {
  const klass = risk === "high"
    ? "border-rose-200/25 bg-rose-400/10 text-rose-100"
    : risk === "medium"
      ? "border-amber-200/25 bg-amber-400/10 text-amber-100"
      : "border-emerald-200/25 bg-emerald-400/10 text-emerald-100";
  return <span className={`rounded-full border px-2.5 py-1 text-[0.65rem] font-black uppercase ${klass}`}>{risk}</span>;
}

function formatPrice(priceUsd: string) {
  if (priceUsd === "0") return "Free";
  if (priceUsd.includes("USD")) return priceUsd;
  return `USD ${priceUsd}`;
}

function defaultWorkflow(kind: ProductKind) {
  if (kind === "messaging") {
    return [
      "El usuario escribe, manda audio o adjunta contexto por WhatsApp/Telegram.",
      "El backend identifica workspace, contacto, thread y herramienta.",
      "La herramienta produce borrador, alerta, schedule o acción sugerida.",
      "Las acciones sensibles requieren aprobación humana.",
      "El workspace guarda historial, auditoría y próximos pasos.",
    ];
  }
  return [
    "El usuario aterriza desde pauta y entiende el resultado esperado.",
    "Elige módulo, agrega input y crea checkout o usa suscripción.",
    "El backend valida pago, idempotencia, rate limit y fraude.",
    "El producto ejecuta workers internos y genera output/artifacts.",
    "El run queda registrado con auditoría y consumo del workspace.",
  ];
}
